import React, { useState, useEffect, useMemo } from "react";
import { Tabs, Form, Input, Button, Switch, message, Table, Modal, Select, Popconfirm, InputNumber, Tag, Space, Typography} from "antd";
import AxiosOICStat from "AxiosOICStat";
import { useSelector } from "react-redux";
import { GenerateTable } from "./GenerateTable";
import DatePicker from 'react-multi-date-picker';
import DatePanel from "react-multi-date-picker/plugins/date_panel"
import GenerateContentPage from "./GenerateContentPage";
import SybContentOrder from "./SybContentOrder";
import GenerateReport from "./GenerateReport";
import OperatorExpressionInput from "components/OperatorExpressionInput";
import { getIndicatorName } from "../utils/indicatorName";

const { TabPane } = Tabs;
const { Option } = Select;
const { Text } = Typography;

export function Syb() {
  const parseOperatorExpression = (raw) => {
    if (!raw && raw !== 0) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "object") return raw;
    if (typeof raw === "string") {
      const trimmed = raw.trim();
      if (!trimmed) return [];
      try {
        // Try JSON first (when backend stored JSON string)
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        // not JSON, try regex like: operator='/' constant=1.0
        const m = trimmed.match(/operator=["'](?<op>[^"']+)["']\s+constant=(?<const>[\d\.]+)/);
        if (m && m.groups) {
          return [{ operator: m.groups.op, constant: parseFloat(m.groups.const) }];
        }
      }
    }
    return [];
  };
  const [formConfig] = Form.useForm();
  const [formIndicator] = Form.useForm();
  const [activeTab, setActiveTab] = useState("1");
  const [configurations, setConfigurations] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [isConfigModalVisible, setConfigModalVisible] = useState(false);
  const [isConfigEditModalVisible, setConfigEditModalVisible] = useState(false);
  const [isIndicatorModalVisible, setIndicatorModalVisible] = useState(false);
  const [isIndicatorEditModalVisible, setIndicatorEditModalVisible] = useState(false);
  const [selectedYears, setSelectedYears] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchConfigText, setSearchConfigText] = useState("");
  const [editingIndicator, setEditingIndicator] = useState(null);


  const indicatorsState = useSelector((state) => state.indicators);
  // Fetch existing configurations
  const fetchConfigurations = async () => {
    try {
      const response = await AxiosOICStat.get("/syb/syb_configs/");
      console.log("Fetched configurations:", response.data);
      setConfigurations(response.data);
    } catch (error) {
      //message.error("Failed to fetch configurations: " + error.message);
    }
  };

  // Fetch existing indicators
  const fetchIndicators = async () => {
    try {
      const response = await AxiosOICStat.get("/syb/syb_indicators/");
      setIndicators(response.data);
    } catch (error) {
      //message.error("Failed to fetch indicators: " + error.message);
    }
  };

  useEffect(() => {
    fetchConfigurations();
    fetchIndicators();
    // restore last active tab from localStorage
    try {
      const saved = localStorage.getItem("sybActiveTab");
      if (saved) setActiveTab(saved);
    } catch (e) {
      // ignore if localStorage not available
    }
  }, []);

  const handleAddConfig = async (values) => {
    const yearToString = (d) => {
      if (!d) return "";
      if (d.year) return d.year.toString();
      const dt = new Date(d);
      return isNaN(dt.getFullYear()) ? String(d) : String(dt.getFullYear());
    };
    if (Array.isArray(values.years)) {
      values.years = values.years.map((year) => yearToString(year)).join(",");
    }
    try {
      await AxiosOICStat.post("/syb/syb_configs/", values);
      message.success("Configuration added successfully!");
      formConfig.resetFields();
      await fetchConfigurations(); // Refresh configurations
      setConfigModalVisible(false);
    } catch (error) {
      message.error("Failed to add configuration: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddIndicator = async (values) => {
    const yearToString = (d) => {
      if (!d) return "";
      if (d.year) return d.year.toString();
      const dt = new Date(d);
      return isNaN(dt.getFullYear()) ? String(d) : String(dt.getFullYear());
    };
    if (Array.isArray(values.years)) {
      values.years = values.years.map((year) => yearToString(year)).join(",");
    }

    if (indicators.some(i => i.ind_code === values.ind_code)) {
      formIndicator.setFields([
        { name: 'ind_code', errors: ['Indicator already exists'] }
      ]);
      return;
    }

    const payload = {
      ind_code: values.ind_code,
      config_id: values.config_id,
      operator_expression: parseOperatorExpression(values.operator_expression),
      is_unit_conversion_needed: !!values.is_unit_conversion_needed,
      is_oic_calc_needed: !!values.is_oic_calc_needed,
      is_world_calc_needed: !!values.is_world_calc_needed,
      is_world_percentage_needed: !!values.is_world_percentage_needed,
      decimal_places: values.decimal_places ?? 2,
      years: values.years || "",
    };
    try {
      await AxiosOICStat.post("/syb/syb_indicators/", payload);
      message.success("Indicator added successfully!");
      formIndicator.resetFields();
      fetchIndicators(); // Refresh indicators
      setIndicatorModalVisible(false);
    } catch (error) {
      message.error("Failed to add indicator: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteConfig = async (config_id) => {
    try {
      await AxiosOICStat.delete(`/syb/syb_configs/${config_id}`);
      message.success("Configuration deleted successfully!");
      fetchConfigurations(); // Refresh configurations
    } catch (error) {
      message.error("Failed to delete configuration: " + error.message);
    }
  };

  const handleDeleteIndicator = async (ind_code, config_id) => {
    try {
      await AxiosOICStat.delete(`/syb/syb_indicators/`, {
        data: { ind_code, config_id },
      });
      message.success("Indicator deleted successfully!");
      fetchIndicators(); // Refresh indicators
    } catch (error) {
      message.error("Failed to delete indicator: " + error.message);
    }
  };

  const handleEditConfig = async (values) => {
    console.log("Updating config with values:", values);
    // Convert the years array into a single string
    const yearToString = (d) => {
      if (!d) return "";
      if (d.year) return d.year.toString();
      const dt = new Date(d);
      return isNaN(dt.getFullYear()) ? String(d) : String(dt.getFullYear());
    };
    const yearsString = Array.isArray(values.years) ? values.years.map(y => yearToString(y)).join(',') : values.years;
    
    if (!values.config_id) {
      console.error("Config ID is missing. Cannot update config.");
      return;
    }
    
    try {
      const response = await AxiosOICStat.put(`/syb/syb_configs/${values.config_id}`, {
        config_name: values.config_name,
        is_oic_calc_needed: values.is_oic_calc_needed,
        is_world_calc_needed: values.is_world_calc_needed,
        is_world_percentage_needed: values.is_world_percentage_needed,
        decimal_places: values.decimal_places,
        years: yearsString,
        operator_expression: values.operator_expression,
      });
    
      message.success("Configuration updated successfully!");
      setConfigEditModalVisible(false);
      await fetchConfigurations(); // Refresh configurations if needed
    } catch (error) {
      if (error.response) {
        console.error("Validation error details:", error.response.data);
      } else {
        console.error("Error updating config:", error);
      }
    }
  };
  
  const handleOpenEditModal = (record) => {
    const yearsArray = record.years ? record.years.split(',') : [];
    const yearsAsDates = yearsArray.map(y => {
      const ynum = parseInt(y);
      if (isNaN(ynum)) return null;
      return new Date(ynum, 0, 1);
    }).filter(Boolean);
    formConfig.setFieldsValue({
      ...record,
      years: yearsAsDates,
    });
    setSelectedYears(yearsAsDates);
    setConfigEditModalVisible(true);
  };
    
  

  const enrichedIndicators = indicators.map((indicator) => {
    const config = configurations.find((c) => c.config_id === indicator.config_id) || null;
    const indicatorDetails = indicatorsState.value[indicator.ind_code] || {};

    return {
      ...indicator,
      config: config, // attach the full config object (or null)
      config_name: config?.config_name || "Custom Configuration", // Add config name
      indicator_name: getIndicatorName(indicatorDetails) || "Unknown Indicator", // Add indicator name
    };
  });

  const filteredConfigurations = useMemo(() => {
    if (!searchConfigText) return configurations;
    const lower = searchConfigText.toLowerCase();
    return configurations.filter((c) => {
      return String(c.config_id).toLowerCase().includes(lower) || (c.config_name || "").toLowerCase().includes(lower);
    });
  }, [configurations, searchConfigText]);

  const configColumns = [
    { title: "ID", dataIndex: "config_id", key: "config_id" },
    { title: "Name", dataIndex: "config_name", key: "config_name" },
    { title: "OIC Calculation Needed", dataIndex: "is_oic_calc_needed", key: "is_oic_calc_needed", render: (val) => (val ? "Yes" : "No") },
    { title: "World Calculation Needed", dataIndex: "is_world_calc_needed", key: "is_world_calc_needed", render: (val) => (val ? "Yes" : "No") },
    { title: "Is World Percentage Needed", dataIndex: "is_world_percentage_needed", key: "is_world_percentage_needed", render: (val) => (val ? "Yes" : "No") },
    { title: "Decimal Place", dataIndex: "decimal_places", key: "decimal_places"},
    //{
    //  title: "Operator Expression",
    //  dataIndex: "operator_expression",
    //  key: "operator_expression",
    //  render: (ops) => {
    //    if (!ops || ops.length === 0) return "";
    //    return ops.map(op => `${op.operator} ${op.constant}`).join(", ");
    //  }
    //}
    //,    
    { title: "Selected Years", dataIndex: "years", key: "years"},
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Button style={{ marginRight: 10 }} onClick={() => handleOpenEditModal(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this configuration?"
            onConfirm={() => handleDeleteConfig(record.config_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger>Delete</Button>
          </Popconfirm>
        </>
      ),
    }    

  ];

  const handleOpenIndicatorEditModal = (record) => {
    setEditingIndicator(record);
    const yearsArray = record.years ? record.years.split(',') : [];
    const yearsAsDates = yearsArray.map(y => {
      const ynum = parseInt(y);
      if (isNaN(ynum)) return null;
      return new Date(ynum, 0, 1);
    }).filter(Boolean);
    formIndicator.setFieldsValue({
      ...record,
      operator_expression: parseOperatorExpression(record.operator_expression),
      is_unit_conversion_needed: Boolean(record.is_unit_conversion_needed),
      years: yearsAsDates,
      is_oic_calc_needed: Boolean(record.is_oic_calc_needed),
      is_world_calc_needed: Boolean(record.is_world_calc_needed),
      is_world_percentage_needed: Boolean(record.is_world_percentage_needed),
      decimal_places: record.decimal_places,
    });
    setSelectedYears(yearsAsDates);
    setIndicatorEditModalVisible(true);
  };

  const applyConfigToIndicator = (configId) => {
    const cfg = configurations.find(c => c.config_id === configId);
    if (!cfg) {
      formIndicator.setFieldsValue({
        is_oic_calc_needed: false,
        is_world_calc_needed: false,
        is_world_percentage_needed: false,
        decimal_places: undefined,
        years: [],
      });
      setSelectedYears([]);
      return;
    }
    const yearsArray = cfg.years ? cfg.years.split(',') : [];
    const yearsAsDates = yearsArray.map(y => {
      const ynum = parseInt(y);
      if (isNaN(ynum)) return null;
      return new Date(ynum, 0, 1);
    }).filter(Boolean);

    formIndicator.setFieldsValue({
      is_oic_calc_needed: Boolean(cfg.is_oic_calc_needed),
      is_world_calc_needed: Boolean(cfg.is_world_calc_needed),
      is_world_percentage_needed: Boolean(cfg.is_world_percentage_needed),
      decimal_places: cfg.decimal_places,
      years: yearsAsDates,
    });
    setSelectedYears(yearsAsDates);
  };

  const handleEditIndicator = async values => {
    if (!editingIndicator) return;
    console.log("Updating indicator with values:", values);
    // Ensure operator_expression is an array that backend expects
    const opExpr = parseOperatorExpression(values.operator_expression);
    const yearToString = (d) => {
      if (!d) return "";
      if (d.year) return d.year.toString();
      const dt = new Date(d);
      return isNaN(dt.getFullYear()) ? String(d) : String(dt.getFullYear());
    };
    if (Array.isArray(values.years)) {
      values.years = values.years.map((year) => yearToString(year)).join(",");
    }
    try {
      await AxiosOICStat.put(
        `/syb/syb_indicators/${editingIndicator.ind_code}`,
        {
          config_id:                 values.config_id,
          operator_expression:       opExpr,
          is_unit_conversion_needed: !!values.is_unit_conversion_needed,
          is_oic_calc_needed:        !!values.is_oic_calc_needed,
          is_world_calc_needed:      !!values.is_world_calc_needed,
          is_world_percentage_needed: !!values.is_world_percentage_needed,
          decimal_places:            values.decimal_places ?? 2,
          years:                     values.years || "",
        }
      );
      message.success("Indicator updated successfully!");
      fetchIndicators();
      setIndicatorEditModalVisible(false);
      formIndicator.resetFields();
    } catch (error) {
      message.error(
        "Failed to update indicator: " +
          (error.response?.data?.detail || error.message)
      );
    }
  };
  
  

  const filteredIndicators = useMemo(() => {
    if(!searchText) return enrichedIndicators;
    const lowerSearchText = searchText.toLowerCase();
    return enrichedIndicators.filter((indicator) =>
      String(indicator.ind_code).toLowerCase().includes(lowerSearchText) ||
      indicator.indicator_name.toLowerCase().includes(lowerSearchText) ||
      indicator.config_name.toLowerCase().includes(lowerSearchText)
    );
  }, [enrichedIndicators, searchText]);

  console.log("Filtered indicators:", filteredIndicators);

  const indicatorColumns = [
    { title: "Indicator Code", dataIndex: "ind_code", key: "ind_code", sorter: (a, b) => {
      const aVal = Number(a.ind_code || 0);
      const bVal = Number(b.ind_code || 0);
      return aVal - bVal;
    }},
    { title: "Indicator Name", dataIndex: "indicator_name", key: "indicator_name", sorter: (a, b) => a.indicator_name.localeCompare(b.indicator_name) },
    // { title: "Configuration ID", dataIndex: "config_id", key: "config_id", sorter: (a, b) => a.config_id - b.config_id },
    { title: "Configuration Name", dataIndex: "config_name", key: "config_name", sorter: (a, b) => a.config_name.localeCompare(b.config_name) },
    {
      title: "Operator Expression",
      dataIndex: "operator_expression",
      key: "operator_expression",
      render: expr =>
        Array.isArray(expr)
          ? expr.map(op => `${op.operator} ${op.constant}`).join(", ")
          : expr || ""
    },
    {
      title: "OIC Row Unit Conversion",
      dataIndex: "is_unit_conversion_needed",
      key: "is_unit_conversion_needed",
      render: (_, record) => {
        const val = record.config?.is_unit_conversion_needed ?? record.is_unit_conversion_needed;
        return val ? "Yes" : "No";
      }
    },
    { title: "OIC Calc Needed", dataIndex: "is_oic_calc_needed", key: "is_oic_calc_needed", render: (_, record) => (record.config?.is_oic_calc_needed ?? record.is_oic_calc_needed) ? "Yes" : "No" },
    { title: "World Calc Needed", dataIndex: "is_world_calc_needed", key: "is_world_calc_needed", render: (_, record) => (record.config?.is_world_calc_needed ?? record.is_world_calc_needed) ? "Yes" : "No" },
    { title: "World % Needed", dataIndex: "is_world_percentage_needed", key: "is_world_percentage_needed", render: (_, record) => (record.config?.is_world_percentage_needed ?? record.is_world_percentage_needed) ? "Yes" : "No" },
    { title: "Decimal Places", dataIndex: "decimal_places", key: "decimal_places", render: (_, record) => record.config?.decimal_places ?? record.decimal_places },
    { title: "Years", dataIndex: "years", key: "years", render: (_, record) => record.config?.years ?? record.years },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
        <Button onClick={() => handleOpenIndicatorEditModal(record)}>
           Edit
         </Button>
           <Popconfirm
             title="Are you sure you want to delete this indicator?"
             onConfirm={() => handleDeleteIndicator(record.ind_code, record.config_id)}
             okText="Yes"
             cancelText="No"
           >
             <Button danger>Delete</Button>
           </Popconfirm>
         </Space>
      ),
    },
  ];

  return (
    <div>
      <h1>Statistical Yearbook</h1>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          try {
            localStorage.setItem("sybActiveTab", key);
          } catch (e) {}
        }}
      >
        {/* Tab for displaying configurations */}
        <TabPane tab="Configurations" key="1">
          <Button style={{ marginBottom: 10 }} type="primary" onClick={() => setConfigModalVisible(true)}>
            Add Configuration
          </Button>
          <Input
            placeholder="Search configurations by id or name"
            value={searchConfigText}
            onChange={(e) => setSearchConfigText(e.target.value)}
            style={{ width: 300, marginBottom: 10, marginLeft: 10 }}
          />

          <Space style={{ marginBottom: 10, marginLeft: 10}}>
            <Text strong>Total records:</Text>
            <Tag color="blue">{filteredConfigurations.length}</Tag>
          </Space>
          <Table
            dataSource={filteredConfigurations}
            columns={configColumns}
            rowKey="config_id"
            pagination={{ pageSize: 20 }}
          />
        </TabPane>

        {/* Tab for displaying indicators */}
        <TabPane tab="Indicators" key="2">
          <Button type="primary" style={{ marginBottom: 10 }} onClick={() => { formIndicator.resetFields(); setSelectedYears([]); setIndicatorModalVisible(true); }}>
            Add Indicator
          </Button>

          <Input
            placeholder="Search by code or name"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300, marginBottom: 10, marginLeft: 10 }}
          />

          <Space style={{ marginBottom: 10, marginLeft: 10}}>
            <Text strong>Total records:</Text>
            <Tag color="blue">{enrichedIndicators.length}</Tag>
          </Space>

          <Table
            dataSource={indicatorsState.status === 'succeeded' ? filteredIndicators : []}
            loading={indicatorsState.status !== 'succeeded'}
            columns={indicatorColumns}
            rowKey={(record) => `${record.ind_code}-${record.config_id}`}
          />
        </TabPane>

        {/* Placeholder for the "Generate Report" tab */}
        <TabPane tab="Generate Table" key="3">
          <GenerateTable />
        </TabPane>

        <TabPane tab="Generate Report" key="5">
          <GenerateReport />
        </TabPane>

        <TabPane tab="Syb Content Order" key="6">
          <SybContentOrder />
        </TabPane>
      </Tabs>
      <Modal
        title="Add Configuration"
        visible={isConfigModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        footer={null}
      >
        <Form
          form={formConfig}
          layout="vertical"
          onFinish={handleAddConfig}
        >
          <Form.Item
            name="config_name"
            label="Configuration Name"
            rules={[{ required: true, message: "Please enter a configuration name" }]}
          >
            <Input placeholder="Enter configuration name" />
          </Form.Item>
          <Form.Item
            name="is_oic_calc_needed"
            label="Is OIC Calculation Needed"
            valuePropName="checked"
          >
            <Switch onChange={() => {
              // user changed a config-derived field -> clear selected config
              // clear selected configuration reliably
              formIndicator.setFields([{ name: 'config_id', value: null }, { name: 'ind_code', errors: [] }]);
            }} />
          </Form.Item>
          <Form.Item
            name="is_world_calc_needed"
            label="Is World Calculation Needed"
            valuePropName="checked"
          >
            <Switch />
            
          </Form.Item>
          <Form.Item
            name="is_world_percentage_needed"
            label="Is World Percentage Needed"
            valuePropName="checked"
          >
            <Switch />
            
          </Form.Item>
          <Form.Item
            name="decimal_places"
            label="Decimal Place"
            rules={[{ required: true, message: "Please enter an integer" }]}
          >
            <InputNumber style={{ width: "100%" }} placeholder="Enter integer" min={0} />
          </Form.Item>
          <Form.Item
            name="operator_expression"
            label="Operator Expression"
          >
            <OperatorExpressionInput />
          </Form.Item>
          <Form.Item
            name="years"
            label="Select Years"
            rules={[{ required: true, message: "Please select at least one year" }]}
          >
            <DatePicker
              onlyYearPicker
              multiple
              sort
              highlightToday={false}
              placeholder="Please select years"
              value={selectedYears}
              onChange={setSelectedYears}
              maxDate={new Date()}
              minDate={new Date(1970, 0, 1)}
              plugins={[
                <DatePanel />,
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Save Changes
            </Button>
          </Form.Item>
  </Form>
</Modal>

      {/* Modal for editing configuration */}
      <Modal
        title="Edit Configuration"
        visible={isConfigEditModalVisible}
        onCancel={() => setConfigEditModalVisible(false)}
        footer={null}
      >
        <Form
          form={formConfig}
          layout="vertical"
          onFinish={handleEditConfig}
        >
          <Form.Item name="config_id" style={{ display: "none" }}>
            <Input />
          </Form.Item>
          <Form.Item
            name="config_name"
            label="Configuration Name"
            rules={[{ required: true, message: "Please enter a configuration name" }]}
          >
            <Input placeholder="Enter configuration name" />
          </Form.Item>
          <Form.Item
            name="is_oic_calc_needed"
            label="Is OIC Calculation Needed"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="is_world_calc_needed"
            label="Is World Calculation Needed"
            valuePropName="checked"
          >
            <Switch onChange={() => {
              formIndicator.setFields([{ name: 'config_id', value: null }, { name: 'ind_code', errors: [] }]);
            }} />
          </Form.Item>
          <Form.Item
            name="is_world_percentage_needed"
            label="Is World Percentage Needed"
            valuePropName="checked"
          >
            <Switch onChange={() => {
              formIndicator.setFields([{ name: 'config_id', value: null }, { name: 'ind_code', errors: [] }]);
            }} />
          </Form.Item>
          <Form.Item
            name="decimal_places"
            label="Decimal Place"
            rules={[{ required: true, message: "Please enter an integer" }]}
          >
            <InputNumber style={{ width: "100%" }} placeholder="Enter integer" min={0} />
          </Form.Item>
          <Form.Item
            name="operator_expression"
            label="Operator Expression"
          >
            <OperatorExpressionInput />
          </Form.Item>
          <Form.Item
            name="years"
            label="Select Years"
            rules={[{ required: true, message: "Please select at least one year" }]}
          >
            <DatePicker
              onlyYearPicker
              multiple
              sort
              highlightToday={false}
              placeholder="Please select years"
              value={selectedYears}
              onChange={setSelectedYears}
              maxDate={new Date()}
              minDate={new Date(1970, 0, 1)}
              plugins={[
                <DatePanel />,
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </Modal>



      {/* Modal for adding indicator */}
      <Modal
        title="Add Indicator"
        visible={isIndicatorModalVisible}
        onCancel={() => setIndicatorModalVisible(false)}
        footer={null}
      >
        <Form
          form={formIndicator}
          layout="vertical"
          onFinish={handleAddIndicator}
        >
          <Form.Item
            name="ind_code"
            label="Indicator Code"
            rules={[{ required: true, message: "Please select an indicator code" }]}
          >
            <Select
              showSearch
              autoClearSearchValue={false}
              placeholder="Select Indicator Code"
              optionFilterProp="label"
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              onChange={(val) => {
                // clear any duplicate validation error when user selects a different indicator
                formIndicator.setFields([{ name: 'ind_code', errors: [] }]);
                // immediately validate duplicate (use current config_id, may be undefined/null)
                const cfgId = formIndicator.getFieldValue('config_id');
                const exists = indicators.some(i => i.ind_code === val);
                if (exists) {
                  formIndicator.setFields([{ name: 'ind_code', errors: ['This indicator already exists'] }]);
                }
              }}
            >
              {Object.values(indicatorsState.value || {}).map((indicator) => (
                <Option
                  key={indicator.ind_code}
                  value={indicator.ind_code}
                  label={`${indicator.ind_code} - ${getIndicatorName(indicator)}`}
                >
                  {indicator.ind_code} - {getIndicatorName(indicator)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="config_id"
            label="Configuration ID"
          >
            <Select
              allowClear
              placeholder="Select Configuration ID (optional)"
              onChange={(val) => {
                const cfg = configurations.find(c => c.config_id === val);
                // clear any duplicate validation error when config changes
                formIndicator.setFields([{ name: 'ind_code', errors: [] }]);
                if (!cfg) {
                  // clear any config-derived fields
                  formIndicator.setFieldsValue({
                    is_oic_calc_needed: false,
                    is_world_calc_needed: false,
                    is_world_percentage_needed: false,
                    decimal_places: undefined,
                    years: [],
                  });
                  setSelectedYears([]);
                }
                // immediately validate duplicate using selected ind_code (global check)
                const indVal = formIndicator.getFieldValue('ind_code');
                const exists = indicators.some(i => i.ind_code === indVal);
                if (indVal && exists) {
                  formIndicator.setFields([{ name: 'ind_code', errors: ['This indicator already exists'] }]);
                }
                if (!cfg) return;
                // populate indicator fields from selected configuration
                const yearsArray = cfg.years ? cfg.years.split(',') : [];
                const yearsAsDates = yearsArray.map(y => {
                  const ynum = parseInt(y);
                  if (isNaN(ynum)) return null;
                  return new Date(ynum, 0, 1);
                }).filter(Boolean);

                formIndicator.setFieldsValue({
                  is_oic_calc_needed: Boolean(cfg.is_oic_calc_needed),
                  is_world_calc_needed: Boolean(cfg.is_world_calc_needed),
                  is_world_percentage_needed: Boolean(cfg.is_world_percentage_needed),
                  decimal_places: cfg.decimal_places,
                  years: yearsAsDates,
                });
                setSelectedYears(yearsAsDates);
              }}
            >
              {configurations.map((config) => (
                <Option key={config.config_id} value={config.config_id}>
                  {config.config_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="is_unit_conversion_needed"
            label="OIC Row Unit Conversion (thousand->million etc)"
            valuePropName="checked"
          >
            <Switch onChange={() => {
              formIndicator.setFields([{ name: 'config_id', value: null }, { name: 'ind_code', errors: [] }]);
            }} />
          </Form.Item>
          <Form.Item
            name="operator_expression"
            label="Operator Expression"
          >
            <OperatorExpressionInput />
          </Form.Item>

          <Form.Item
            name="is_oic_calc_needed"
            label="Is OIC Calculation Needed"
            valuePropName="checked"
          >
            <Switch onChange={() => {
              formIndicator.setFields([{ name: 'config_id', value: null }, { name: 'ind_code', errors: [] }]);
            }} />
          </Form.Item>
          <Form.Item
            name="is_world_calc_needed"
            label="Is World Calculation Needed"
            valuePropName="checked"
          >
            <Switch onChange={() => {
              formIndicator.setFields([{ name: 'config_id', value: null }, { name: 'ind_code', errors: [] }]);
            }} />
          </Form.Item>
          <Form.Item
            name="is_world_percentage_needed"
            label="Is World Percentage Needed"
            valuePropName="checked"
          >
            <Switch onChange={() => {
              formIndicator.setFields([{ name: 'config_id', value: null }, { name: 'ind_code', errors: [] }]);
            }} />
          </Form.Item>
          <Form.Item
            name="decimal_places"
            label="Decimal Place"
          >
            <InputNumber style={{ width: "100%" }} placeholder="Enter integer" min={0}
              onChange={() => {
                formIndicator.setFields([{ name: 'config_id', value: null }, { name: 'ind_code', errors: [] }]);
              }}
            />
          </Form.Item>
          <Form.Item
            name="years"
            label="Select Years"
          >
            <DatePicker
              onlyYearPicker
              multiple
              sort
              highlightToday={false}
              placeholder="Please select years"
              value={selectedYears}
              onChange={(val) => { 
                setSelectedYears(val);
                formIndicator.setFieldsValue({ years: val });
                formIndicator.setFields([{ name: 'config_id', value: null }, { name: 'ind_code', errors: [] }]);
              }}
              maxDate={new Date()}
              minDate={new Date(1970, 0, 1)}
              plugins={[
                <DatePanel />,
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Add Indicator
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
  title="Edit Indicator"
  visible={isIndicatorEditModalVisible}
  onCancel={() => setIndicatorEditModalVisible(false)}
  footer={null}
>
  <Form
    form={formIndicator}
    layout="vertical"
    onFinish={handleEditIndicator}
  >
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <Form.Item
        name="config_id"
        label="Configuration"
        style={{ flex: 1, marginBottom: 0 }}
      >
        <Select allowClear placeholder="Select configuration (optional)" onChange={(val) => applyConfigToIndicator(val)}>
          {configurations.map(c => (
            <Option key={c.config_id} value={c.config_id}>
              {c.config_name}
            </Option>
          ))}
        </Select>
      </Form.Item>
    </div>

    <Form.Item
      name="operator_expression"
      label="Operator Expression"
    >
      <OperatorExpressionInput />
    </Form.Item>

    <Form.Item
      name="is_unit_conversion_needed"
      label="OIC Row Unit Conversion (thousand->million etc)"
      valuePropName="checked"
    >
      <Switch />
    </Form.Item>

    <Form.Item
      name="is_oic_calc_needed"
      label="Is OIC Calculation Needed"
      valuePropName="checked"
    >
      <Switch onChange={() => {
        formIndicator.setFields([{ name: 'config_id', value: null }, { name: 'ind_code', errors: [] }]);
      }} />
    </Form.Item>
    <Form.Item
      name="is_world_calc_needed"
      label="Is World Calculation Needed"
      valuePropName="checked"
    >
      <Switch onChange={() => {
        formIndicator.setFields([{ name: 'config_id', value: null }, { name: 'ind_code', errors: [] }]);
      }} />
    </Form.Item>
    <Form.Item
      name="is_world_percentage_needed"
      label="Is World Percentage Needed"
      valuePropName="checked"
    >
      <Switch onChange={() => {
        formIndicator.setFields([{ name: 'config_id', value: null }, { name: 'ind_code', errors: [] }]);
      }} />
    </Form.Item>
    <Form.Item
      name="decimal_places"
      label="Decimal Place"
    >
      <InputNumber style={{ width: "100%" }} placeholder="Enter integer" min={0}
        onChange={() => {
          formIndicator.setFields([{ name: 'config_id', value: null }, { name: 'ind_code', errors: [] }]);
        }}
      />
    </Form.Item>
    <Form.Item
      name="years"
      label="Select Years"
    >
      <DatePicker
        onlyYearPicker
        multiple
        sort
        highlightToday={false}
        placeholder="Please select years"
        value={selectedYears}
        onChange={(val) => { setSelectedYears(val); formIndicator.setFieldsValue({ years: val }); formIndicator.setFields([{ name: 'config_id', value: null }, { name: 'ind_code', errors: [] }]); }}
        maxDate={new Date()}
        minDate={new Date(1970, 0, 1)}
        plugins={[
          <DatePanel />,
        ]}
      />
    </Form.Item>

    <Form.Item>
      <Button type="primary" htmlType="submit">
        Save Changes
      </Button>
    </Form.Item>
  </Form>
</Modal>

    </div>
  );
}
